import "./WindowGlobalFix";
import "./App.css";
import "./index.css";

import { ApolloProvider } from "@apollo/client";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter5Adapter } from "use-query-params/adapters/react-router-5";
import { parse, stringify } from "query-string";
import { Provider } from "react-redux";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { asyncWithLDProvider, LDEvaluationDetail } from "launchdarkly-react-client-sdk";
import { createRoot } from "react-dom/client";
import {
  EventInterceptionPlugin,
  FlagOverridePlugin,
  useLaunchDarklyToolbar,
} from "@launchdarkly/toolbar";

import "./App.css";
import { ErrorBoundary } from "./components/errorBoundary";
import App from "./containers/app";
import { GraphQlClient } from "./graphql/client";
import "./index.css";
import store from "./store";
import meta from "./utils/meta";

import { ChatProvider } from "./containers/chat/chat";
import { QueryProvider } from "./providers/QueryProvider";

import { routes } from "@src/appV2/redesign/utils/routes";
import { Guest } from "./guest";
import { MaterialUiProviders } from "./mui/providers/MaterialUiProviders";
import { loadLazily } from "./utils/loadLazily";
import { type ReactNode, Suspense } from "react";
import { FallbackLoader } from "./components/lazyLoad";
import { environmentConfig, isDevelopmentNodeEnvironment } from "./appV2/environment";
import { Helmet } from "react-helmet";
import { LD_USER_KIND } from "./appV2/FeatureFlags/cbhUserContext";
import { ImpersonationSessionsOverlay } from "./appV2/ImpersonationSessions/ImpersonationSessionsOverlay";
import { ImpersonationSessionsProvider } from "./appV2/ImpersonationSessions/ImpersonationSessionsProvider";
import { GlobalPollingProvider } from "./appV2/polling/useGlobalPolling";
import { datadogRum } from "@datadog/browser-rum";
import { DeepLinkProvider } from "./mobile/providers/DeepLinkProvider";
import { Platform } from "./mobile/utils/platform";
import { SplashScreen } from "@capacitor/splash-screen";

const flagOverridePlugin = new FlagOverridePlugin();
const eventInterceptionPlugin = new EventInterceptionPlugin();

interface LaunchDarklyToolbarWrapperProps {
  children: ReactNode;
}

function LaunchDarklyToolbarWrapper({ children }: LaunchDarklyToolbarWrapperProps) {
  useLaunchDarklyToolbar({
    flagOverridePlugin,
    eventInterceptionPlugin,
    position: "bottom-right",
    enabled: isDevelopmentNodeEnvironment(),
  });

  return <>{children}</>;
}

const DocumentProcessorContainer = loadLazily(() =>
  import("./containers/documentProcessor/DocumentProcessorContainer").then((module) => ({
    default: module.DocumentProcessorContainer,
  }))
);

const container = document.getElementById("root");
if (!container) {
  throw new Error("#root is not found");
}
const root = createRoot(container);

const { api, app } = meta();

window.global.api = api;
window.global.app = app;
void (async () => {
  const LDProvider = await asyncWithLDProvider({
    context: { anonymous: true, kind: LD_USER_KIND },
    reactOptions: {
      useCamelCaseFlagKeys: false,
    },
    clientSideID: environmentConfig.REACT_APP_LAUNCH_DARKLY_CLIENT_SIDE_ID,
    options: {
      evaluationReasons: true,
      plugins: isDevelopmentNodeEnvironment() ? [flagOverridePlugin, eventInterceptionPlugin] : [],
      inspectors: [
        {
          type: "flag-used",
          name: "flag-inspector",
          method: async (key: string, detail: LDEvaluationDetail) => {
            datadogRum.addFeatureFlagEvaluation(key, detail.value);
          },
        },
      ],
    },
  });

  root.render(
    <ErrorBoundary>
      {/* we will replace apollo with react-query since react-query has better support for both REST and graphql */}
      <ApolloProvider client={GraphQlClient}>
        <QueryProvider>
          <Provider store={store}>
            <Helmet>
              <title>Clipboard</title>
            </Helmet>
            <BrowserRouter basename={process.env.REACT_APP_BASE_PATH || "/"}>
              <QueryParamProvider
                adapter={ReactRouter5Adapter}
                options={{
                  searchStringToObject: parse,
                  objectToSearchString: stringify,
                }}
              >
                <DeepLinkProvider>
                  <LDProvider>
                    <LaunchDarklyToolbarWrapper>
                      <ChatProvider>
                        <MaterialUiProviders>
                          <GlobalPollingProvider>
                            <ImpersonationSessionsProvider>
                              <ImpersonationSessionsOverlay />
                              <Switch>
                                {/* add Guest's path to satisfy Switch's requirement for Route direct children
                          https://github.com/remix-run/react-router/blob/v5/packages/react-router/modules/Switch.js#L27 */}
                                <Guest
                                  path={[
                                    routes.workplaceGetAccess,
                                    routes.workplaceReferrals,
                                    routes.shiftBoard,
                                  ]}
                                />
                                <Route exact path={routes.verifyDocument}>
                                  <Suspense fallback={<FallbackLoader />}>
                                    <DocumentProcessorContainer />
                                  </Suspense>
                                </Route>
                                <App />
                              </Switch>
                            </ImpersonationSessionsProvider>
                          </GlobalPollingProvider>
                        </MaterialUiProviders>
                      </ChatProvider>
                    </LaunchDarklyToolbarWrapper>
                  </LDProvider>
                </DeepLinkProvider>
              </QueryParamProvider>
            </BrowserRouter>
          </Provider>
        </QueryProvider>
      </ApolloProvider>
    </ErrorBoundary>
  );

  // Hide splash screen once React has rendered (mobile only)
  if (Platform.isMobile) {
    // Use a small delay to ensure first paint has occurred
    setTimeout(() => {
      SplashScreen.hide().catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("Failed to hide splash screen:", err);
      });
    }, 100);
  }
})();
